import React, { useState, useEffect, useContext, useCallback } from 'react';
import { ClipboardList, Calendar, Filter, Download, Package, TrendingUp, Search, ChevronDown, MapPin, User } from 'lucide-react';
import Layout from '../components/Layout';
import api from '../utils/api';
import { AuthContext } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { canAccessBossModules } from '../utils/roleUtils';

const ShipmentReports = () => {
    const { user } = useContext(AuthContext);
    const [reportData, setReportData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedSeller, setSelectedSeller] = useState('');
    const [sellers, setSellers] = useState([]);
    const [activePeriod, setActivePeriod] = useState('monthly');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [showDetails, setShowDetails] = useState(false);

    const periods = [
        { key: 'weekly', label: 'Haftalık', icon: <Calendar size={14} /> },
        { key: 'monthly', label: 'Aylık', icon: <Calendar size={14} /> },
        { key: 'ytd', label: 'Yıl Başından Beri', icon: <TrendingUp size={14} /> },
    ];

    const routeColors = {
        'Ambar': 'bg-purple-500/10 text-purple-400 border-purple-500/20',
        'Yurtiçi Kargo': 'bg-blue-500/10 text-blue-400 border-blue-500/20',
        'Sürat Kargo': 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
        'MNG Kargo': 'bg-orange-500/10 text-orange-400 border-orange-500/20',
        'Aras Kargo': 'bg-red-500/10 text-red-400 border-red-500/20',
        'DHL / Yurtdışı': 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
    };
    const defaultRouteColor = 'bg-slate-500/10 text-slate-400 border-slate-500/20';

    const fetchReport = useCallback(async () => {
        setIsLoading(true);
        try {
            const params = {};
            if (selectedSeller) params.seller = selectedSeller;

            if (startDate && endDate) {
                params.startDate = startDate;
                params.endDate = endDate;
            } else {
                params.period = activePeriod;
            }

            const res = await api.get('/shipments/report', { params });
            setReportData(res.data);
            if (res.data.sellers && sellers.length === 0) {
                setSellers(res.data.sellers);
            }
        } catch (error) {
            console.error('Rapor alınamadı:', error);
            toast.error('Rapor verisi yüklenemedi.');
        } finally {
            setIsLoading(false);
        }
    }, [activePeriod, endDate, selectedSeller, sellers.length, startDate]);

    useEffect(() => {
        fetchReport();
    }, [fetchReport]);

    const handleCustomDateSearch = () => {
        if (startDate && endDate) {
            setActivePeriod('');
            fetchReport();
        } else {
            toast.error('Lütfen başlangıç ve bitiş tarihlerini seçin.');
        }
    };

    const handlePeriodClick = (key) => {
        setStartDate('');
        setEndDate('');
        setActivePeriod(key);
    };

    const handleExport = () => {
        if (!reportData?.details || reportData.details.length === 0) {
            toast.error('Dışa aktarılacak veri yok.');
            return;
        }
        const exportData = reportData.details.map(row => ({
            'Tarih': row.createdAt ? new Date(row.createdAt).toLocaleDateString('tr-TR') : '-',
            'Satıcı': row.seller,
            'Alıcı': row.buyer,
            'Gönderim Yolu': row.route,
            'Ödeme': row.payer,
            'Şehir': row.city,
            'Çuval Sayısı': row.sackCount,
            'Ekleyen': row.createdBy || 'Bilinmiyor',
        }));
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Sevkiyat Raporu');
        XLSX.writeFile(wb, `Sevkiyat_Raporu_${reportData.seller}_${reportData.startDate}_${reportData.endDate}.xlsx`);
        toast.success('Rapor indirildi.');
    };

    if (!canAccessBossModules(user?.role)) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-[60vh]">
                    <p className="text-dark-muted text-lg">Bu sayfaya erişim yetkiniz bulunmamaktadır.</p>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            {/* HEADER */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                    <h1 className="text-xl md:text-3xl font-bold text-dark-text mb-2 tracking-tight flex items-center gap-3">
                        <div className="p-2.5 rounded-xl bg-primary-500/10 border border-primary-500/20">
                            <ClipboardList size={24} className="text-primary-500" />
                        </div>
                        Sevkiyat Raporları
                    </h1>
                    <p className="text-dark-muted text-sm md:text-base">Satıcı bazlı, gönderim yolu kırılımlı sevkiyat analiz ekranı.</p>
                </div>
                <button
                    onClick={handleExport}
                    disabled={!reportData?.details?.length}
                    className="flex items-center gap-2 bg-green-600/20 hover:bg-green-600 border border-green-500/50 text-green-400 hover:text-white px-5 py-2.5 rounded-xl font-bold tracking-wide transition-all shadow-lg hover:scale-105 active:scale-95 disabled:opacity-40 disabled:hover:scale-100"
                >
                    <Download size={18} />
                    <span className="text-sm">EXCEL İNDİR</span>
                </button>
            </div>

            {/* FİLTRE PANELİ */}
            <div className="glass p-5 rounded-2xl mb-6 border border-dark-border space-y-4">
                <div className="flex flex-col md:flex-row gap-4 items-end">
                    {/* Satıcı Seçimi */}
                    <div className="flex-1 min-w-[200px]">
                        <label className="block text-xs font-semibold text-dark-muted mb-2 uppercase tracking-wider">Satıcı</label>
                        <div className="relative">
                            <select
                                value={selectedSeller}
                                onChange={(e) => setSelectedSeller(e.target.value)}
                                className="w-full px-4 py-3 bg-dark-bg border border-dark-border rounded-xl text-dark-text focus:outline-none focus:ring-2 focus:ring-primary-500/50 appearance-none cursor-pointer"
                            >
                                <option value="">Tüm Satıcılar</option>
                                {sellers.map((s, i) => (
                                    <option key={i} value={s}>{s}</option>
                                ))}
                            </select>
                            <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-muted pointer-events-none" />
                        </div>
                    </div>

                    {/* Dönem Butonları */}
                    <div className="flex gap-2 flex-wrap">
                        {periods.map(p => (
                            <button
                                key={p.key}
                                onClick={() => handlePeriodClick(p.key)}
                                className={`flex items-center gap-1.5 px-4 py-3 rounded-xl text-sm font-semibold transition-all border ${activePeriod === p.key
                                        ? 'bg-primary-500 text-white border-primary-500 shadow-lg shadow-primary-500/25'
                                        : 'bg-dark-bg border-dark-border text-dark-muted hover:text-dark-text hover:border-primary-500/30'
                                    }`}
                            >
                                {p.icon}
                                {p.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Özel Tarih Aralığı */}
                <div className="flex flex-col sm:flex-row gap-3 items-end pt-2 border-t border-dark-border/50">
                    <div className="flex-1">
                        <label className="block text-xs font-semibold text-dark-muted mb-2 uppercase tracking-wider">Başlangıç</label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-full px-4 py-3 bg-dark-bg border border-dark-border rounded-xl text-dark-text focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                        />
                    </div>
                    <div className="flex-1">
                        <label className="block text-xs font-semibold text-dark-muted mb-2 uppercase tracking-wider">Bitiş</label>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-full px-4 py-3 bg-dark-bg border border-dark-border rounded-xl text-dark-text focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                        />
                    </div>
                    <button
                        onClick={handleCustomDateSearch}
                        className="flex items-center gap-2 px-5 py-3 bg-primary-500 hover:bg-primary-400 text-white rounded-xl font-semibold transition-all hover:scale-105 active:scale-95 whitespace-nowrap"
                    >
                        <Search size={16} />
                        Rapor Al
                    </button>
                </div>
            </div>

            {/* YÜKLEME DURUMU */}
            {isLoading && (
                <div className="flex justify-center py-16">
                    <div className="w-10 h-10 rounded-full border-t-2 border-r-2 border-primary-500 animate-spin"></div>
                </div>
            )}

            {/* RAPOR İÇERİĞİ */}
            {!isLoading && reportData && (
                <>
                    {/* DÖNEM BİLGİSİ */}
                    <div className="mb-6 flex items-center gap-2 text-dark-muted text-sm">
                        <Filter size={14} />
                        <span>
                            <strong className="text-dark-text">{reportData.seller}</strong>
                            {' · '}
                            {reportData.startDate && new Date(reportData.startDate).toLocaleDateString('tr-TR')}
                            {' – '}
                            {reportData.endDate && new Date(reportData.endDate).toLocaleDateString('tr-TR')}
                        </span>
                    </div>

                    {/* GENEL ÖZET KARTLARI */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                        <div className="glass p-5 rounded-2xl border border-dark-border">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-bold uppercase tracking-wider text-dark-muted">Toplam Sevkiyat</span>
                                <Package size={20} className="text-primary-500" />
                            </div>
                            <p className="text-3xl font-bold text-dark-text">{reportData.grandTotalCount}</p>
                            <p className="text-xs text-dark-muted mt-1">adet gönderi</p>
                        </div>
                        <div className="glass p-5 rounded-2xl border border-dark-border">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-bold uppercase tracking-wider text-dark-muted">Toplam Çuval</span>
                                <ClipboardList size={20} className="text-primary-500" />
                            </div>
                            <p className="text-3xl font-bold text-dark-text">{reportData.grandTotalSacks}</p>
                            <p className="text-xs text-dark-muted mt-1">adet çuval</p>
                        </div>
                    </div>

                    {/* GÖNDERİM YOLU KIRILIM KARTLARI */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                        {reportData.routes && reportData.routes.map((route, idx) => (
                            <div key={idx} className="glass p-5 rounded-2xl border border-dark-border hover:border-primary-500/30 transition-all group">
                                <div className="flex items-center justify-between mb-3">
                                    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border ${routeColors[route.route] || defaultRouteColor}`}>
                                        <Package size={14} />
                                        {route.route}
                                    </span>
                                </div>
                                <div className="flex items-end justify-between">
                                    <div>
                                        <p className="text-2xl font-bold text-dark-text">{route.totalSacks}</p>
                                        <p className="text-xs text-dark-muted">çuval</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-lg font-semibold text-dark-text">{route.count}</p>
                                        <p className="text-xs text-dark-muted">gönderi</p>
                                    </div>
                                </div>
                            </div>
                        ))}

                        {reportData.routes && reportData.routes.length === 0 && (
                            <div className="col-span-full text-center py-12 text-dark-muted">
                                <Package size={48} className="mx-auto mb-4 opacity-30" />
                                <p className="text-lg font-medium">Bu dönem için sevkiyat verisi bulunamadı.</p>
                            </div>
                        )}
                    </div>

                    {/* DETAY TABLOSU */}
                    {reportData.details && reportData.details.length > 0 && (
                        <div className="glass rounded-2xl border border-dark-border overflow-hidden">
                            <button
                                onClick={() => setShowDetails(!showDetails)}
                                className="w-full flex items-center justify-between p-5 text-dark-text font-semibold hover:bg-dark-hover transition-colors"
                            >
                                <span className="flex items-center gap-2">
                                    <Filter size={16} className="text-primary-500" />
                                    Detaylı Kayıtlar ({reportData.details.length} adet)
                                </span>
                                <ChevronDown size={18} className={`transition-transform ${showDetails ? 'rotate-180' : ''}`} />
                            </button>

                            {showDetails && (
                                <>
                                <div className="mobile-table-hint lg:hidden">Tüm sevkiyat ayrıntıları için tabloyu yatay kaydırın →</div>
                                <div className="mobile-table-scroll overflow-x-auto border-t border-dark-border">
                                    <table className="mobile-complete-table w-full text-left border-collapse text-sm whitespace-nowrap">
                                        <thead>
                                            <tr className="bg-dark-card text-dark-muted border-b border-dark-border">
                                                <th className="px-4 py-3 font-semibold uppercase tracking-wider text-xs">Tarih</th>
                                                <th className="px-4 py-3 font-semibold uppercase tracking-wider text-xs">Satıcı</th>
                                                <th className="px-4 py-3 font-semibold uppercase tracking-wider text-xs">Alıcı</th>
                                                <th className="px-4 py-3 font-semibold uppercase tracking-wider text-xs">Gönderim</th>
                                                <th className="px-4 py-3 font-semibold uppercase tracking-wider text-xs">Şehir</th>
                                                <th className="px-4 py-3 font-semibold uppercase tracking-wider text-xs text-center">Çuval</th>
                                                <th className="px-4 py-3 font-semibold uppercase tracking-wider text-xs">Ekleyen</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-dark-border text-dark-text">
                                            {reportData.details.map((row) => (
                                                <tr key={row.id} className="hover:bg-dark-hover transition-colors">
                                                    <td className="px-4 py-3 text-dark-muted text-xs">{new Date(row.createdAt).toLocaleDateString('tr-TR')}</td>
                                                    <td className="px-4 py-3 font-medium">{row.seller}</td>
                                                    <td className="px-4 py-3">{row.buyer}</td>
                                                    <td className="px-4 py-3">
                                                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold border ${routeColors[row.route] || defaultRouteColor}`}>
                                                            <Package size={12} />
                                                            {row.route}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <span className="flex items-center gap-1 text-dark-muted">
                                                            <MapPin size={14} />
                                                            {row.city}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 text-center font-bold">{row.sackCount}</td>
                                                    <td className="px-4 py-3">
                                                        <span className="flex items-center gap-1 text-dark-muted text-xs">
                                                            <User size={12} />
                                                            {row.createdBy || 'Bilinmiyor'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                </>
                            )}
                        </div>
                    )}
                </>
            )}
        </Layout>
    );
};

export default ShipmentReports;
